import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import {
  getCoachTeams,
  getTeamDetails,
  createTeam,
  getClubSettings,
  getClubTeams,
  getClubAthletes,
  type CoachTeam,
  type TeamDetails,
  type ClubAthlete,
} from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import TeamInviteLink from "../../../components/app/TeamInviteLink";
import CoachJoinRequests from "../../../components/app/CoachJoinRequests";
import MobileAppLayout from "../../../layouts/MobileAppLayout";
import { GameButton, GameCard, GameHero } from "../../../components/game";
import { useAgeGroupOptions } from "../../../hooks/useAgeGroupOptions";
import { useSportNames } from "../../../components/SportChips";

// Default sport types (fallback if club has no settings)
const DEFAULT_SPORT_TYPES = [
  "Fotbal",
  "Baschet",
  "Volei",
  "Handbal",
  "Tenis",
  "Natație",
  "Atletism",
  "Gimnastică",
  "Judo",
  "Karate",
  "Box",
  "Ciclism",
  "Altul",
];


interface TeamWithStats {
  id: string;
  name: string;
  age_group: string | null;
  sport_type: string | null;
  athleteCount: number;
  sessionCount: number;
}

export default function AppCoachTeams() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, clubs: ctxClubs, activeClubId: ctxClubId } = useAuth();

  const [teams, setTeams] = useState<TeamWithStats[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<TeamDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Club settings for sports and age groups.
  //
  // `clubs.sports` holds sport IDs (older clubs still hold names), so the raw
  // value is a UUID more often than not. It stays the option's VALUE — the
  // server resolves either spelling — and only the label is translated.
  const [sportTypes, setSportTypes] = useState<string[]>(DEFAULT_SPORT_TYPES);
  const sportName = useSportNames();
  // Club-configured groups (often empty); merged with the platform list below.
  const [clubAgeGroups, setClubAgeGroups] = useState<string[]>([]);

  // Platform-wide age groups merged with anything the club configured, so the
  // picker is never empty and always offers every supported group.
  const ageGroups = useAgeGroupOptions(clubAgeGroups);

  // Create team modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [teamForm, setTeamForm] = useState({
    name: "",
    sport_type: "",
    age_group: "",
    description: "",
  });

  const activeClubId = ctxClubId || ctxClubs[0]?.id;
  const selectedTeamId = searchParams.get("team");

  useEffect(() => {
    const loadData = async () => {
      if (!activeClubId || !user?.id) return;
      setLoading(true);
      setError(false);
      try {
        const [coachTeamsData, settingsData] = await Promise.all([
          getCoachTeams(user.id),
          getClubSettings(activeClubId).catch(() => null), // Gracefully handle if settings don't exist
        ]);

        const teamsWithStats: TeamWithStats[] = coachTeamsData.map((team: CoachTeam) => ({
          id: team.id,
          name: team.name,
          age_group: team.age_group,
          sport_type: team.sport_type,
          athleteCount: team.athlete_count,
          sessionCount: 0, // Will be populated from team details if needed
        }));

        setTeams(teamsWithStats);

        // Use club settings if available, otherwise keep defaults
        if (settingsData) {
          if (settingsData.sports?.length > 0) {
            setSportTypes(settingsData.sports);
          }
          if (settingsData.age_groups?.length > 0) {
            setClubAgeGroups(settingsData.age_groups);
          }
        }
      } catch (error) {
        console.error("Failed to load data:", error);
        setError(true);
      }
      setLoading(false);
    };
    loadData();
  }, [activeClubId, user?.id]);

  // Load team details when a team is selected
  useEffect(() => {
    const loadTeamDetails = async () => {
      if (!selectedTeamId) {
        setSelectedTeam(null);
        return;
      }
      setLoadingDetails(true);
      try {
        const details = await getTeamDetails(selectedTeamId);
        setSelectedTeam(details);
      } catch (error) {
        console.error("Failed to load team details:", error);
      }
      setLoadingDetails(false);
    };
    loadTeamDetails();
  }, [selectedTeamId]);

  const handleSelectTeam = (teamId: string) => {
    setSearchParams({ team: teamId });
  };

  const handleCloseDetails = () => {
    setSearchParams({});
  };

  const handleOpenCreateModal = () => {
    setTeamForm({ name: "", sport_type: "", age_group: "", description: "" });
    setCreateError("");
    setShowCreateModal(true);
  };

  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!teamForm.name.trim()) {
      setCreateError(t("coach.teamNameRequired", "Numele echipei este obligatoriu"));
      return;
    }
    if (!activeClubId) {
      setCreateError(t("coach.noClubSelected", "Nu ai niciun club selectat"));
      return;
    }

    setCreating(true);
    setCreateError("");
    try {
      await createTeam(activeClubId, {
        name: teamForm.name.trim(),
        sport_type: teamForm.sport_type || undefined,
        age_group: teamForm.age_group || undefined,
      });

      // Reload ONLY the coach's teams (mirrors the initial load) — previously
      // this pulled every club team, so creating one made the whole club roster
      // appear.
      const coachTeamsData = user?.id ? await getCoachTeams(user.id) : [];
      const teamsWithStats: TeamWithStats[] = coachTeamsData.map((team: CoachTeam) => ({
        id: team.id,
        name: team.name,
        age_group: team.age_group,
        sport_type: team.sport_type,
        athleteCount: team.athlete_count,
        sessionCount: 0,
      }));
      setTeams(teamsWithStats);

      setShowCreateModal(false);
      setTeamForm({ name: "", sport_type: "", age_group: "", description: "" });
    } catch (err: any) {
      console.error("Failed to create team:", err);
      setCreateError(err.response?.data?.error || t("coach.createTeamError", "Nu s-a putut crea echipa"));
    }
    setCreating(false);
  };

  return (
    <MobileAppLayout>
      <div className="pb-6">
        {/* Header */}
        <GameHero flow className="rounded-br-3xl px-4 pt-3 pb-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h1 className="text-xl font-extrabold">{t("coach.teams", "Echipe")}</h1>
              <p className="text-white/70 text-[13px] mt-0.5">{teams.length} {t("coach.totalTeams", "echipe")}</p>
            </div>
            <button
              onClick={handleOpenCreateModal}
              className="flex items-center gap-1.5 px-3 py-2 bg-white/15 hover:bg-white/25 text-white rounded-xl font-bold text-[13px] transition-colors shrink-0"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {t("coach.createTeam", "Echipă nouă")}
            </button>
          </div>
        </GameHero>

        <div className="px-4 pt-5 space-y-4">
        {/* Teams List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-[3px] border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : error ? (
          <GameCard className="p-6 text-center">
            <h3 className="text-base font-bold text-slate-800 mb-1">{t("common.loadErrorTitle", "Ceva n-a mers")}</h3>
            <p className="text-sm text-slate-500">
              {t("common.loadError", "Nu am putut încărca datele. Verifică conexiunea și încearcă din nou.")}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-3 px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white"
            >
              {t("common.retry", "Reîncearcă")}
            </button>
          </GameCard>
        ) : teams.length === 0 ? (
          <GameCard className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-2xl flex items-center justify-center">
              <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <p className="text-slate-500">{t("coach.noTeams", "Nu ai echipe asignate")}</p>
          </GameCard>
        ) : (
          <div className="space-y-3">
            {teams.map(team => (
              <button
                key={team.id}
                onClick={() => handleSelectTeam(team.id)}
                className="w-full text-left bg-white rounded-2xl border border-slate-100 shadow-[0_3px_0_#eef2f8] active:translate-y-[2px] transition-transform p-4"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center text-white shadow-[0_3px_0_#3730a3]">
                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-slate-800">{team.name}</h3>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {team.sport_type && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">
                          {sportName(team.sport_type)}
                        </span>
                      )}
                      {team.age_group && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">
                          {team.age_group}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-extrabold text-indigo-600">{team.athleteCount}</span>
                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">{t("coach.athletes", "sportivi")}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Team Details Modal */}
        {selectedTeamId && (
          <div className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center pt-[calc(env(safe-area-inset-top,0px)+1rem)] !mt-0">
            {/* Use flex-col + max-h so the body scrolls but the header and
                the bottom CTA bar stay pinned at top/bottom of the modal. */}
            <div className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-2xl max-h-full flex flex-col">
              {/* Header */}
              <div className="flex-shrink-0 bg-white px-4 py-4 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-800">
                  {selectedTeam?.name || t("coach.loading", "Se încarcă...")}
                </h2>
                <button
                  onClick={handleCloseDetails}
                  className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                >
                  <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {loadingDetails ? (
                <div className="flex-1 flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-[3px] border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : selectedTeam ? (
                <div className="flex-1 overflow-y-auto p-4 space-y-6">
                  {/* Team Info */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                        <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">{t("coach.sport", "Sport")}</p>
                        <p className="font-medium text-slate-800">{selectedTeam.sport_type ? sportName(selectedTeam.sport_type) : "-"}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">{t("coach.ageGroup", "Grupa de vârstă")}</p>
                        <p className="font-medium text-slate-800">{selectedTeam.age_group || "-"}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">{t("coach.sessions", "Sesiuni")}</p>
                        <p className="font-medium text-slate-800">{selectedTeam._count?.sessions || 0}</p>
                      </div>
                    </div>
                  </div>

                  {/* The link comes first: it is how a team actually gets
                      filled, and the coach is the one with the parents'
                      group chat. */}
                  <TeamInviteLink
                    teamId={selectedTeamId!}
                    teamName={selectedTeam.name}
                    clubName={(selectedTeam as any).club?.name}
                  />

                  <CoachJoinRequests clubId={activeClubId} teamId={selectedTeamId!} />

                  {/* Athletes List */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-slate-800">
                        {t("coach.athletes", "Sportivi")} ({selectedTeam.athletes?.length || 0})
                      </h3>
                    </div>

                    {selectedTeam.athletes && selectedTeam.athletes.length > 0 ? (
                      <div className="space-y-2">
                        {selectedTeam.athletes.map(athlete => {
                          // Row is now tappable — routes to the same
                          // per-athlete metrics page the coach already
                          // has on /app/coach/athletes. athlete.id here
                          // is the user_id (backend maps it that way in
                          // /users/:userId/coach-teams), so no extra
                          // lookup is needed.
                          const userId = athlete.user?.id || athlete.id;
                          return (
                            <button
                              key={athlete.id}
                              type="button"
                              onClick={() => navigate(`/app/coach/athletes/${userId}/metrics`)}
                              className="w-full text-left flex items-center gap-3 p-3 bg-slate-50 hover:bg-slate-100 active:scale-[0.99] rounded-xl transition-all"
                            >
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-semibold text-sm">
                                {athlete.user?.full_name?.charAt(0) || "?"}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-slate-800 truncate">
                                  {athlete.user?.full_name || t("coach.unnamed", "Fără nume")}
                                </p>
                              </div>
                              <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-6 bg-slate-50 rounded-xl">
                        <svg className="w-12 h-12 text-slate-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        <p className="text-slate-500 text-sm">{t("coach.noAthletes", "Niciun sportiv în echipă")}</p>
                        <p className="mt-2 text-xs text-slate-400">
                          {t("coach.useLinkAbove", "Trimite linkul de mai sus în grupul părinților.")}
                        </p>
                        <Link
                          to={`/app/coach/athletes?action=invite&team=${selectedTeamId}`}
                          className="inline-block mt-3 text-xs font-semibold text-slate-500 underline"
                        >
                          {t("coach.inviteByEmail", "Sau invită pe email")}
                        </Link>
                      </div>
                    )}
                  </div>

                </div>
              ) : null}

              {/* Sticky Bottom CTA — always visible while the team modal is
                  open so the coach has a one-tap path to "Create Session" or
                  "Athletes" regardless of how far they scrolled the body. */}
              {selectedTeam && !loadingDetails && (
                <div
                  className="flex-shrink-0 bg-white border-t border-slate-100 px-4 pt-3"
                  style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1.25rem)" }}
                >
                  <div className="grid grid-cols-2 gap-3">
                    <Link
                      to={`/app/coach/sessions?action=new&team=${selectedTeamId}`}
                      className="flex items-center justify-center gap-2 py-3 text-white bg-gradient-to-b from-emerald-400 to-emerald-500 rounded-2xl font-extrabold text-[15px] shadow-[0_4px_0_#059669] active:translate-y-[3px] transition-transform"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      {t("coach.newSession", "Sesiune")}
                    </Link>
                    <Link
                      to={`/app/coach/athletes?team=${selectedTeamId}`}
                      className="flex items-center justify-center gap-2 py-3 text-white bg-gradient-to-b from-indigo-500 to-blue-600 rounded-2xl font-extrabold text-[15px] shadow-[0_4px_0_#3730a3] active:translate-y-[3px] transition-transform"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      {t("coach.athletes", "Sportivi")}
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Create Team Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center p-4 pt-[calc(env(safe-area-inset-top,0px)+1rem)] sm:pt-4 !mt-0">
            <div
              className="bg-white w-full max-w-lg rounded-2xl shadow-xl flex flex-col"
              style={{ maxHeight: "100%" }}
            >
              {/* Header */}
              <div className="flex-shrink-0 px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-800">
                  {t("coach.createTeam", "Creează echipă")}
                </h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                >
                  <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-5">
                <form id="create-team-form" onSubmit={handleCreateTeam} className="space-y-5">
                  {createError && (
                    <div className="p-3 bg-red-50 text-red-600 rounded-xl text-sm flex items-center gap-2">
                      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {createError}
                    </div>
                  )}

                  {/* Team Name */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("coach.teamName", "Numele echipei")} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={teamForm.name}
                      onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
                      placeholder={t("coach.teamNamePlaceholder", "ex: Juniori A")}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                      required
                    />
                  </div>

                  {/* Sport Type */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("coach.sportType", "Tipul sportului")}
                    </label>
                    <div className="relative">
                      <select
                        value={teamForm.sport_type}
                        onChange={(e) => setTeamForm({ ...teamForm, sport_type: e.target.value })}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all appearance-none cursor-pointer"
                      >
                        <option value="">{t("coach.selectSport", "Selectează sportul")}</option>
                        {sportTypes.map(sport => (
                          <option key={sport} value={sport}>{sportName(sport)}</option>
                        ))}
                      </select>
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                        <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Age Group */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("coach.ageGroup", "Grupa de vârstă")}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {ageGroups.map(group => (
                        <button
                          key={group}
                          type="button"
                          onClick={() => setTeamForm({ ...teamForm, age_group: teamForm.age_group === group ? "" : group })}
                          className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                            teamForm.age_group === group
                              ? "bg-indigo-500 text-white"
                              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                          }`}
                        >
                          {group}
                        </button>
                      ))}
                    </div>
                  </div>

                </form>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-5 py-4 border-t border-slate-100 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 py-3 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-colors"
                >
                  {t("common.cancel", "Anulează")}
                </button>
                <GameButton
                  variant="primary"
                  type="submit"
                  form="create-team-form"
                  disabled={creating || !teamForm.name.trim()}
                  className="flex-1"
                >
                  {creating ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      {t("common.creating", "Se creează...")}
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      {t("coach.createTeam", "Creează echipa")}
                    </>
                  )}
                </GameButton>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </MobileAppLayout>
  );
}
